import { ArrowRightIcon, Button, CheckIcon, CloseIcon, Header, Paragraph, RichTextDisplay } from "@datagame/ui";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import styles from "./ResultModal.module.scss";
import { GLAUKOS_PROJECT_ID_PRODUCTION, GLAUKOS_PROJECT_ID_STAGING } from "../../../main";

const cx = classNames.bind(styles);

export interface ResultModalProps {
  isCorrect: boolean;
  explanation: string;
  isLastQuestion: boolean;
  onContinue: () => void;
  theme?: string;
}

const TRANSITION = {
  duration: 0.5,
  ease: "easeInOut",
};

const MODAL_ANIMATION_VARIANTS = {
  hidden: {
    left: "50%",
    top: "0",
    transform: "translate(-50%, -100%)",
    transition: TRANSITION,
  },
  visible: {
    top: "50%",
    transform: "translate(-50%, -50%)",
    transition: TRANSITION,
  },
};

const BACKGROUND_ANIMATION_VARIANTS = {
  hidden: {
    opacity: 0,
    transition: TRANSITION,
  },
  visible: {
    opacity: 1,
    transition: TRANSITION,
  },
};

export const ResultModal = ({ isCorrect, explanation, isLastQuestion, onContinue, theme }: ResultModalProps) => {
  const { t } = useTranslation("trivia");
  const isDesktop = useMediaQuery({ query: "(min-width: 1280px)" });

  const icon = isCorrect ? <CheckIcon /> : <CloseIcon />;

  const isGlaukosProject =
    window.location.pathname.includes(GLAUKOS_PROJECT_ID_PRODUCTION) ||
    window.location.pathname.includes(GLAUKOS_PROJECT_ID_STAGING);

  return (
    <div className={styles.resultModal}>
      <motion.div
        variants={BACKGROUND_ANIMATION_VARIANTS}
        initial={"hidden"}
        animate={"visible"}
        exit={"hidden"}
        className={styles.background}
      />
      <motion.div
        variants={MODAL_ANIMATION_VARIANTS}
        initial={"hidden"}
        animate={"visible"}
        exit={"hidden"}
        className={styles.modal}
      >
        <Header className={cx("header", { isCorrect, isGlaukosProject })} variant={isDesktop ? "h4" : "h5"}>
          {isCorrect ? t("modal.correct") : t("modal.wrong")}
          {icon}
        </Header>
        <Paragraph size={isDesktop ? "large" : "regular"} className={styles.explanation}>
          <RichTextDisplay value={explanation} />
        </Paragraph>

        <Button
          className={styles.continueButton}
          icon={<ArrowRightIcon />}
          size={isDesktop ? "large" : "medium"}
          onClick={onContinue}
          style={{ background: theme }}
        >
          {isLastQuestion ? t("modal.finish") : t("modal.continue")}
        </Button>
      </motion.div>
    </div>
  );
};
